/**
 * Responsive Image Helper
 * 
 * Generates srcSet strings for images stored in Supabase Storage,
 * using the Image Transformation API for on-the-fly resizing & quality control.
 */

const STORAGE_OBJECT_URL = 'https://nzvyxkbsowtinhywugwn.supabase.co/storage/v1/object/public/blog-images';
const STORAGE_RENDER_URL = 'https://nzvyxkbsowtinhywugwn.supabase.co/storage/v1/render/image/public/blog-images';

/**
 * Converts a Supabase Storage object URL to an optimized render URL
 * with width and quality parameters.
 *
 * The Supabase render endpoint automatically negotiates the best modern
 * image format (AVIF / WebP / JPEG) based on the browser's `Accept`
 * request header — no explicit `format` parameter required.
 */
export function getOptimizedStorageUrl(
  url: string,
  width?: number,
  quality: number = 70
): string {
  // Only transform Supabase storage object URLs
  const objectPrefix = 'supabase.co/storage/v1/object/public/';
  const idx = url.indexOf(objectPrefix);
  if (idx === -1) return url;

  const renderUrl = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  const params = new URLSearchParams();
  if (width) params.set('width', String(width));
  params.set('quality', String(quality));
  return `${renderUrl}?${params.toString()}`;
}

/**
 * Width sets for the different image roles in the blog.
 * Each set covers 1x AND 2x (Retina) rendering for its layout.
 */
export const VARIANT_WIDTHS = {
  /** Blog cards in grids (~387px slot → up to 2x on large screens) */
  card: [320, 480, 640, 800, 1024, 1200],
  /** Article hero / featured image (~800px slot → 2x = 1600) */
  featured: [640, 800, 1024, 1280, 1600, 2048],
  /** Full-bleed images */
  full: [640, 960, 1280, 1600, 1920, 2560],
} as const;

export type VariantPreset = keyof typeof VARIANT_WIDTHS;

/**
 * Generates a responsive srcSet using Supabase Image Transformations
 * for any storage object URL.
 *
 * Format is negotiated automatically by the render endpoint (AVIF → WebP →
 * original) based on the browser `Accept` header, so one srcSet serves all.
 */
export function getStorageSrcSet(
  url: string,
  widths: readonly number[] = VARIANT_WIDTHS.full,
  quality: number = 70
): string {
  const objectPrefix = 'supabase.co/storage/v1/object/public/';
  if (!url.includes(objectPrefix)) return '';

  const renderUrl = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  return [...widths]
    .map((w) => `${renderUrl}?width=${w}&quality=${quality} ${w}w`)
    .join(', ');
}

const STORAGE_BASE_URL = STORAGE_OBJECT_URL;

// Standard breakpoints for responsive images
export const IMAGE_WIDTHS = [640, 1024, 1920] as const;

export type ImageWidth = typeof IMAGE_WIDTHS[number];

/**
 * Generates a srcSet string for an image with multiple size variants
 * 
 * @param baseName - The base filename without extension (e.g., "hero-banner")
 * @param extension - The file extension (e.g., "jpg")
 * @param widths - Array of available widths (default: [640, 1024, 1920])
 * @returns srcSet string for use in img tag
 * 
 * @example
 * generateSrcSet('drk-Domino_21', 'jpg')
 * // Returns: "https://.../drk-Domino_21-640.jpg 640w, https://.../drk-Domino_21-1024.jpg 1024w, https://.../drk-Domino_21.jpg 1920w"
 */
export function generateSrcSet(
  baseName: string,
  extension: string = 'jpg',
  widths: readonly number[] = IMAGE_WIDTHS
): string {
  return widths
    .map((width, index) => {
      // Last width is the original/full-size image (no suffix)
      const suffix = index === widths.length - 1 ? '' : `-${width}`;
      const url = `${STORAGE_BASE_URL}/${baseName}${suffix}.${extension}`;
      return `${url} ${width}w`;
    })
    .join(', ');
}

/**
 * Generates a srcSet string for local assets with size variants
 * 
 * @param imports - Object with width keys and imported image URLs
 * @returns srcSet string
 * 
 * @example
 * import portrait320 from '@/assets/portrait-320.png';
 * import portrait640 from '@/assets/portrait-640.png';
 * import portrait from '@/assets/portrait.png';
 * 
 * generateLocalSrcSet({
 *   320: portrait320,
 *   640: portrait640,
 *   1024: portrait
 * })
 */
export function generateLocalSrcSet(
  imports: Record<number, string>
): string {
  return Object.entries(imports)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([width, url]) => `${url} ${width}w`)
    .join(', ');
}

/**
 * Generates appropriate sizes attribute based on common layouts
 */
export const SIZES_PRESETS = {
  // Full width on all devices
  fullWidth: '100vw',
  
  // Hero portrait (small on mobile, medium on desktop)
  heroPortrait: '(max-width: 640px) 224px, (max-width: 768px) 256px, (max-width: 1024px) 288px, 288px',
  
  // Half width on desktop, full on mobile
  halfWidth: '(max-width: 768px) 100vw, 50vw',
  
  // Third width on desktop
  thirdWidth: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  
  // Blog featured image
  blogFeatured: '(max-width: 768px) 100vw, (max-width: 1200px) 720px, 800px',
  
  // Card thumbnail
  cardThumbnail: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
} as const;

/**
 * Gets the storage URL for an image
 */
export function getStorageImageUrl(filename: string): string {
  return `${STORAGE_BASE_URL}/${filename}`;
}
